import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, User, Tag, ArrowLeft, Clock, BookOpen } from "lucide-react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500&display=swap');

  .blog-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .blog-root { font-family: 'DM Sans', sans-serif; }
  .blog-heading { font-family: 'Playfair Display', serif; }

  .spinner {
    width: 36px; height: 36px;
    border: 3px solid #e7e5e4; border-top-color: #b45309;
    border-radius: 50%; animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .detail-hero { width: 100%; height: 420px; object-fit: cover; display: block; }
  @media (max-width: 600px) { .detail-hero { height: 240px; } }

  .detail-body { max-width: 760px; margin: 0 auto; padding: 48px 24px 100px; }

  .back-btn {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; font-weight: 500; letter-spacing: 0.06em;
    text-transform: uppercase; color: #78716c;
    background: none; border: none; cursor: pointer;
    text-decoration: none;
    transition: color 0.18s; font-family: 'DM Sans', sans-serif;
    margin-bottom: 28px; padding: 0;
  }
  .back-btn:hover { color: #b45309; }

  .blog-section-heading {
    font-family: 'Playfair Display', serif;
    font-size: 22px; font-weight: 700;
    color: #1c1917; margin: 34px 0 12px; line-height: 1.3;
  }
  .blog-section-subheading {
    font-family: 'Playfair Display', serif;
    font-size: 17px; font-weight: 600; font-style: italic;
    color: #44403c; margin: 26px 0 10px; line-height: 1.4;
  }
  .blog-para { margin-bottom: 16px; font-size: 16px; color: #44403c; line-height: 1.85; font-weight: 300; }
  .blog-para:last-child { margin-bottom: 0; }
  .blog-bullet {
    display: flex; gap: 10px; margin-bottom: 7px;
    font-size: 15.5px; color: #44403c; line-height: 1.7; font-weight: 300;
  }
  .blog-bullet-dot { color: #b45309; font-weight: 600; flex-shrink: 0; }
  .blog-numbered { margin-bottom: 8px; font-size: 15.5px; font-weight: 500; color: #1c1917; line-height: 1.6; }

  .badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 4px 10px; background: rgba(180,83,9,0.9); color: #fff;
    font-size: 10px; font-weight: 600;
    letter-spacing: 0.08em; text-transform: uppercase;
    font-family: 'DM Sans', sans-serif;
  }
`;

// Turns raw content text into styled blocks. Headings/subheadings are
// stored as plain-text markers inside the `content` field itself
// (## for heading, ### for subheading) — no backend/schema change
// needed, this is just parsed back out on render. Content written
// before this change (no markers) still renders fine via the old
// heuristic fallback further down.
function parseContent(raw) {
  if (!raw) return [];
  const lines = raw.split(/\r?\n/).map((l) => l.trim());
  const blocks = [];
  for (const line of lines) {
    if (!line) continue;

    if (line.startsWith("### ")) {
      blocks.push({ type: "subheading", text: line.replace(/^###\s+/, "") });
    } else if (line.startsWith("## ")) {
      blocks.push({ type: "heading", text: line.replace(/^##\s+/, "") });
    } else if (line.startsWith("•\t") || line.startsWith("• ")) {
      blocks.push({ type: "bullet", text: line.replace(/^•[\t ]/, "") });
    } else if (/^\d+\.\s/.test(line) && line.length < 120) {
      blocks.push({ type: "numbered", text: line });
    } else if (
      // Fallback heuristic for older posts written without explicit
      // ## / ### markers: short, punctuation-free lines read as headings.
      line.length < 90 &&
      /^[A-Z0-9]/.test(line) &&
      !line.endsWith(".") &&
      !line.endsWith(",") &&
      !line.endsWith(":") &&
      line.split(" ").length < 12
    ) {
      blocks.push({ type: "heading", text: line });
    } else {
      blocks.push({ type: "para", text: line });
    }
  }
  return blocks;
}

// URL is /blogs/some-title-slug-<mongoId>. The last hyphen-separated
// chunk is the real id we look the post up by.
function extractId(slugId = "") {
  const parts = slugId.split("-");
  return parts[parts.length - 1];
}

export default function BlogDetailPage() {
  const { slugId } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const id = extractId(slugId);
    setLoading(true);
    setNotFound(false);

    // Prefer a single-post endpoint if the API has one; fall back to
    // fetching the list and matching by id so this works either way.
    fetch(`https://archpoint.onrender.com/api/blogs/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error("not found via direct endpoint");
        return res.json();
      })
      .then((data) => { setBlog(data); setLoading(false); })
      .catch(() => {
        fetch("https://archpoint.onrender.com/api/blogs")
          .then((res) => res.json())
          .then((list) => {
            const match = list.find((b) => b._id === id);
            if (match) setBlog(match); else setNotFound(true);
            setLoading(false);
          })
          .catch(() => { setNotFound(true); setLoading(false); });
      });
  }, [slugId]);

  useEffect(() => {
    if (blog?.title) document.title = `${blog.title} | Arch Point Consultants`;
  }, [blog]);

  if (loading) {
    return (
      <div className="blog-root">
        <style>{styles}</style>
        <div style={{ display: "flex", justifyContent: "center", padding: "140px 0", background: "#fafaf9", minHeight: "100vh" }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (notFound || !blog) {
    return (
      <div className="blog-root">
        <style>{styles}</style>
        <div style={{ textAlign: "center", padding: "140px 24px", background: "#fafaf9", minHeight: "100vh", color: "#a8a29e" }}>
          <BookOpen size={40} style={{ margin: "0 auto 16px", opacity: 0.4 }} />
          <p style={{ marginBottom: "20px" }}>This article couldn't be found.</p>
          <Link to="/blogs" className="back-btn" style={{ justifyContent: "center" }}>
            <ArrowLeft size={13} /> Back to all articles
          </Link>
        </div>
      </div>
    );
  }

  const blocks = parseContent(blog.content);
  const readTime = Math.max(1, Math.ceil((blog.content || "").split(/\s+/).length / 200));

  return (
    <div className="blog-root">
      <style>{styles}</style>
      <div style={{ background: "#fafaf9", minHeight: "100vh" }}>
        {blog.imageUrl && (
          <div style={{ position: "relative" }}>
            <img src={blog.imageUrl} alt={blog.title} className="detail-hero" />
            <div style={{ position: "absolute", bottom: "20px", left: "24px" }}>
              <span className="badge"><Tag size={9} /> {blog.category}</span>
            </div>
          </div>
        )}

        <div className="detail-body">
          <button className="back-btn" onClick={() => navigate("/blogs")}>
            <ArrowLeft size={13} /> All Articles
          </button>

          <h1
            className="blog-heading"
            style={{ fontSize: "clamp(1.6rem, 3.4vw, 2.4rem)", fontWeight: 700, color: "#1c1917", lineHeight: 1.25, marginBottom: "20px" }}
          >
            {blog.title}
          </h1>

          {/* Meta */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", marginBottom: "32px", paddingBottom: "24px", borderBottom: "1px solid #e7e5e4" }}>
            {[
              { icon: <User size={13} />, text: blog.author },
              { icon: <Calendar size={13} />, text: new Date(blog.createdAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) },
              { icon: <Clock size={13} />, text: `${readTime} min read` },
            ].map((m, i) => (
              <span key={i} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "#78716c" }}>
                <span style={{ color: "#b45309" }}>{m.icon}</span>{m.text}
              </span>
            ))}
          </div>

          {/* Summary pull quote */}
          {blog.summary && (
            <div style={{ borderLeft: "3px solid #b45309", paddingLeft: "20px", marginBottom: "36px" }}>
              <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: "16px", color: "#57534e", lineHeight: 1.75 }}>
                {blog.summary.split(/\r?\n/)[0]}
              </p>
            </div>
          )}

          {/* Full Content */}
          <div>
            {blocks.map((block, i) => {
              if (block.type === "heading")
                return <p key={i} className="blog-section-heading">{block.text}</p>;
              if (block.type === "subheading")
                return <p key={i} className="blog-section-subheading">{block.text}</p>;
              if (block.type === "bullet")
                return (
                  <div key={i} className="blog-bullet">
                    <span className="blog-bullet-dot">—</span>
                    <span>{block.text}</span>
                  </div>
                );
              if (block.type === "numbered")
                return <p key={i} className="blog-numbered">{block.text}</p>;
              return <p key={i} className="blog-para">{block.text}</p>;
            })}
          </div>
        </div>
      </div>
    </div>
  );
}